import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { CreateSavedChecklistInput, SavedChecklist, UpdateSavedChecklistInput } from '@repo/map-user-data/types';
import type {
    CreateMap3dObjectRequest,
    MapData3dObject,
    UpdateMap3dObjectRequest,
    Upload3dObjectResponse,
} from '@repo/types/maps/map-3d-types';
import type {
    CreateMapLabelRequest,
    CreateMapRegionRequest,
    CustomPinProperties,
    GameMapInfo,
    GameMapUserPin,
    MapData,
    MapDataCategory,
    MapDataConfig,
    MapDataLabel,
    MapDataLocationFeature,
    MapDataRegion,
    MapDataSubcategory,
    MapPinSyncResponse,
    NewMapDataLocationFeature,
    PlayerLocationDeltas,
    UpdateMapLabelRequest,
    UpdateMapRegionRequest,
    UploadLabelIconResponse,
} from '@repo/types/maps/map-types';
import { gameCoordsToLatLng } from '@repo/types/maps/map-utils';
import type { MapHeatmapQuery, MapHeatmapResponse } from '@repo/wemod-api/analytics/schema';
import { type AIPromptConfig, getAIPromptConfig } from '@repo/wemod-api-client/ai-prompts-api-client';
import { getAuthJwt } from '@repo/wemod-api-client/auth-jwt-api-client';
import { getMapHeatmap } from '@repo/wemod-api-client/heatmap-api-client';
import type { BulkUpdateGameMapPin } from '@repo/wemod-api-client/maps-api-client';
import {
    bulkMoveGameMapPins,
    bulkUpdateGameMapPins,
    bulkUpdatePinStatuses,
    createGameMapCategory,
    createGameMapLabel,
    createGameMapLocationFeature,
    createGameMapRegion,
    createGameMapSubcategory,
    createMap3dObject,
    createUserCustomMarker,
    deleteGameMapCategory,
    deleteGameMapLabel,
    deleteGameMapLocationFeature,
    deleteGameMapRegion,
    deleteGameMapSubcategory,
    deleteMap3dObject,
    deleteUserCustomMarker,
    getCustomUserPin,
    getGameMap,
    getGameMapMetadata,
    getGameMaps,
    getUserCustomMarkers,
    getUserMapPins,
    resetFoundPins,
    setMapPinFound,
    updateGameMapCategory,
    updateGameMapLabel,
    updateGameMapLocationFeature,
    updateGameMapRegion,
    updateGameMapSubcategory,
    updateMap3dObject,
    updateUserCustomMarker,
    upload3dObject,
    uploadLabelIcon,
} from '@repo/wemod-api-client/maps-api-client';
import { type MapDataState, updateFoundPins } from '../slices/mapData';
import { CUSTOM_PINS_CATEGORY_ID, DEFAULT_PIN_COLOR, VIDEO_PINS_SUBCATEGORY_ID } from '../utils/mapData';
import {
    createSavedChecklist,
    deleteSavedChecklist,
    listSavedChecklists,
    updateSavedChecklist,
} from './user-data-client';

export type ConvertCustomMarkerCoordinatesMutation = {
    markerId: string;
    externalId: string;
    playerLocationDeltas: PlayerLocationDeltas;
};

const customBaseQuery = fetchBaseQuery({
    baseUrl: '/', // Not used since we're using custom queryFn
});

export const mapsApi = createApi({
    reducerPath: 'mapsApi',
    baseQuery: customBaseQuery,
    tagTypes: [
        'MapData',
        'MapInfo',
        'MapConfig',
        'FoundPins',
        'CustomMarkers',
        'CustomUserPin',
        'MapRegions',
        'MapLabels',
        'Map3dObjects',
        'SavedChecklists',
        'AIPromptConfig',
        'AuthJwt',
        'MapHeatmap',
    ],
    endpoints: builder => ({
        // Get map data for a specific game map
        getGameMap: builder.query<MapData, { externalId: string; locale?: string; debug?: boolean }>({
            queryFn: async ({ externalId, locale = 'en-US', debug = false }) => {
                try {
                    let token: string | undefined;
                    if (debug && typeof document !== 'undefined') {
                        const tokenMatch = document.cookie.match(/(?:^|; )token=([^;]*)/);
                        token = tokenMatch?.[1] ? decodeURIComponent(tokenMatch[1]) : undefined;
                    }

                    const data = await getGameMap(externalId, locale, debug ? { debug: true, token } : undefined);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            providesTags: (_, __, { externalId }) => [{ type: 'MapData', id: externalId }],
        }),

        // Get map config for a specific game map
        getGameMapMetadata: builder.query<
            MapDataConfig,
            { titleSlug: string; mapSlug: string; locale?: string; debug?: boolean }
        >({
            queryFn: async ({ titleSlug, mapSlug, locale = 'en-US', debug = false }) => {
                try {
                    const data = await getGameMapMetadata({
                        title_slug: titleSlug,
                        map_slug: mapSlug,
                        locale,
                        options: { debug },
                    });
                    return { data: data.map };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            providesTags: (_, __, { titleSlug, mapSlug }) => [{ type: 'MapConfig', id: `${titleSlug}-${mapSlug}` }],
        }),

        // Get map info for a game title
        getGameMaps: builder.query<GameMapInfo[], { titleId: string; locale?: string; debug?: boolean }>({
            queryFn: async ({ titleId, locale = 'en-US', debug = false }) => {
                try {
                    const data = await getGameMaps({ titleId }, locale, debug ? { debug } : undefined);
                    return { data: [...data] };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            providesTags: (_, __, { titleId }) => [{ type: 'MapInfo', id: titleId }],
        }),

        // Get user's found pins for a map
        getFoundPins: builder.query<GameMapUserPin[], string>({
            queryFn: async externalId => {
                try {
                    const data = await getUserMapPins(externalId);

                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            providesTags: (_, __, externalId) => [{ type: 'FoundPins', id: externalId }],
        }),

        // Get user's custom markers for a map
        getCustomMarkers: builder.query<MapDataLocationFeature[], string>({
            queryFn: async externalId => {
                try {
                    const [response, foundPinsResponse] = await Promise.all([
                        getUserCustomMarkers(externalId),
                        getUserMapPins(externalId).catch(() => []), // Fallback to empty array if failed
                    ]);

                    const features = response.geojson.features as MapDataLocationFeature[];

                    const foundCustomPins = new Map<string, boolean>();

                    foundPinsResponse.forEach(pin => {
                        if (pin.pinnableType === 'user_pin') {
                            foundCustomPins.set(pin.pinnableId, pin.found);
                        }
                    });

                    const getSubcategoryExternalId = (feature: MapDataLocationFeature) => {
                        const isVideoPin =
                            feature.properties.creationSource === 'user_video_upload' || !!feature.properties.videoPath;
                        return isVideoPin ? VIDEO_PINS_SUBCATEGORY_ID : CUSTOM_PINS_CATEGORY_ID;
                    };

                    const transformedFeatures = features.map(feature => ({
                        ...feature,
                        properties: {
                            ...feature.properties,
                            isCustomPin: true,
                            selected: false,
                            found: foundCustomPins.get(feature.properties.externalId) || false,
                            subcategoryExternalId: getSubcategoryExternalId(feature),
                        },
                    }));

                    return { data: transformedFeatures };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            providesTags: (_, __, externalId) => [{ type: 'CustomMarkers', id: externalId }],
        }),

        // Toggle pin found status
        togglePinFound: builder.mutation<
            GameMapUserPin,
            { externalId: string; locationId: string; isCustomPin?: boolean; currentFoundState: boolean }
        >({
            queryFn: async ({ externalId, locationId, isCustomPin = false, currentFoundState }) => {
                try {
                    const data = await setMapPinFound(externalId, {
                        pinnableId: locationId,
                        found: !currentFoundState,
                        pinnableType: isCustomPin ? 'user_pin' : 'pin',
                    });

                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { externalId }) => [{ type: 'FoundPins', id: externalId }],
            // Optimistic update
            onQueryStarted: async ({ externalId, locationId, currentFoundState }, { dispatch, queryFulfilled }) => {
                // Update the found pins cache optimistically
                const patchResult = dispatch(
                    mapsApi.util.updateQueryData('getFoundPins', externalId, draft => {
                        const pin = draft.find(p => p.pinnableId === locationId);
                        if (pin) {
                            pin.found = !currentFoundState;
                        }
                    }),
                );

                // Also update the Redux slice's foundPins state optimistically
                dispatch(updateFoundPins({ [locationId]: !currentFoundState }));

                try {
                    await queryFulfilled;
                } catch {
                    // Revert both optimistic updates on error
                    patchResult.undo();
                    dispatch(updateFoundPins({ [locationId]: currentFoundState }));
                }
            },
        }),

        // Reset all found pins
        resetAllFoundPins: builder.mutation<null, string>({
            queryFn: async externalId => {
                try {
                    await resetFoundPins(externalId);
                    return { data: null };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, externalId) => [
                { type: 'FoundPins', id: externalId },
                { type: 'MapData', id: externalId },
            ],
        }),

        bulkUpdatePinStatuses: builder.mutation<
            MapPinSyncResponse,
            {
                externalId: string;
                pins: Pick<GameMapUserPin, 'pinnableType' | 'pinnableId'>[];
                found: boolean;
            }
        >({
            queryFn: async ({ externalId, pins, found }) => {
                try {
                    const data = await bulkUpdatePinStatuses(externalId, pins, found);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { externalId }) => [{ type: 'FoundPins', id: externalId }],
            onQueryStarted: async ({ externalId, pins, found }, { dispatch, queryFulfilled, getState }) => {
                const { foundPins } = (getState() as unknown as { mapData: MapDataState }).mapData;
                const previousFoundStates: Record<string, boolean> = {};
                for (const pin of pins) {
                    previousFoundStates[pin.pinnableId] = foundPins[pin.pinnableId] ?? false;
                }

                const pinUpdates: Record<string, boolean> = {};
                for (const pin of pins) {
                    pinUpdates[pin.pinnableId] = found;
                }

                dispatch(updateFoundPins(pinUpdates));

                const patchResult = dispatch(
                    mapsApi.util.updateQueryData('getFoundPins', externalId, draft => {
                        for (const pin of pins) {
                            const existing = draft.find(p => p.pinnableId === pin.pinnableId);
                            if (existing) {
                                existing.found = found;
                            }
                        }
                    }),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                    dispatch(updateFoundPins(previousFoundStates));
                }
            },
        }),

        // Create custom marker
        createCustomMarker: builder.mutation<
            MapDataLocationFeature,
            {
                externalId: string;
                lng: number;
                lat: number;
                title?: string;
                description?: string;
                color?: string;
            }
        >({
            queryFn: async ({
                externalId,
                lng,
                lat,
                title = 'Custom Marker',
                description = '',
                color = DEFAULT_PIN_COLOR,
            }) => {
                try {
                    const customMarker = await createUserCustomMarker({
                        gameMapExternalId: externalId,
                        properties: { title, description, color },
                        geometry: { type: 'Point', coordinates: [lng, lat] },
                    });

                    const feature: MapDataLocationFeature = {
                        type: 'Feature',
                        geometry: { type: 'Point', coordinates: [lng, lat] },
                        properties: {
                            color: customMarker.properties.color,
                            externalId: customMarker.externalId,
                            subcategoryExternalId: CUSTOM_PINS_CATEGORY_ID,
                            title: customMarker.title,
                            description: customMarker.description,
                            isCustomPin: true,
                            selected: false,
                            found: false,
                            imageId: null,
                            videoId: null,
                            imagePath: null,
                            videoPath: null,
                        },
                    };

                    return { data: feature };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { externalId }) => [
                { type: 'MapData', id: externalId },
                { type: 'CustomMarkers', id: externalId },
                { type: 'FoundPins', id: externalId },
            ],
        }),

        // Update custom marker
        updateCustomMarker: builder.mutation<
            MapDataLocationFeature,
            {
                markerId: string;
                title: string;
                description: string;
                color: string;
                coordinates: [number, number];
                externalId: string;
            }
        >({
            queryFn: async ({ markerId, title, description, color, coordinates }) => {
                try {
                    await updateUserCustomMarker(markerId, {
                        properties: {
                            title,
                            description,
                            color,
                        },
                        geometry: {
                            type: 'Point',
                            coordinates,
                        },
                    });

                    const feature: MapDataLocationFeature = {
                        type: 'Feature',
                        geometry: { type: 'Point', coordinates },
                        properties: {
                            color,
                            externalId: markerId,
                            subcategoryExternalId: CUSTOM_PINS_CATEGORY_ID,
                            title,
                            description,
                            isCustomPin: true,
                            selected: false,
                            found: false,
                            imageId: null,
                            videoId: null,
                            imagePath: null,
                            videoPath: null,
                        },
                    };

                    return { data: feature };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { externalId }) => [
                { type: 'MapData', id: externalId },
                { type: 'CustomMarkers', id: externalId },
                { type: 'FoundPins', id: externalId },
            ],
        }),

        // Delete custom marker
        deleteCustomMarker: builder.mutation<null, { markerId: string; externalId: string }>({
            queryFn: async ({ markerId }) => {
                try {
                    await deleteUserCustomMarker(markerId);
                    return { data: null };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { externalId }) => [
                { type: 'MapData', id: externalId },
                { type: 'CustomMarkers', id: externalId },
                { type: 'FoundPins', id: externalId },
            ],
        }),

        // Convert user pin coordinates from gameCoords to lat/lng
        // Update geometry.coordinates in the database
        convertCustomMarkerCoordinates: builder.mutation<
            { converted: boolean },
            ConvertCustomMarkerCoordinatesMutation
        >({
            queryFn: async ({ markerId, externalId, playerLocationDeltas }) => {
                try {
                    const response = await getUserCustomMarkers(externalId);
                    const features = response.geojson.features as MapDataLocationFeature[];
                    const feature = features.find(f => f.properties.externalId === markerId);

                    if (!feature) {
                        return { error: { status: 'FETCH_ERROR', error: 'Marker not found' } };
                    }

                    if (
                        feature.properties.gameCoords?.x &&
                        feature.properties.gameCoords.y &&
                        feature.properties.gameCoords.z &&
                        feature.geometry.coordinates[0] === 0 &&
                        feature.geometry.coordinates[1] === 0 &&
                        playerLocationDeltas
                    ) {
                        const latLng = gameCoordsToLatLng(feature.properties.gameCoords, playerLocationDeltas.deltas);

                        if (latLng) {
                            const newCoordinates = [latLng.lng, latLng.lat] as [number, number];

                            await updateUserCustomMarker(markerId, {
                                properties: {
                                    color: feature.properties.color ?? DEFAULT_PIN_COLOR,
                                    description: feature.properties.description ?? '',
                                    title: feature.properties.title ?? '',
                                },
                                geometry: {
                                    type: 'Point',
                                    coordinates: newCoordinates,
                                },
                            });

                            return { data: { converted: true } };
                        }
                    }

                    return { data: { converted: false } };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (result, _error, { externalId }) =>
                result?.converted ? [{ type: 'CustomMarkers', id: externalId }] : [],
        }),

        // Get custom user pin
        getCustomUserPin: builder.query<CustomPinProperties, string>({
            queryFn: async pinId => {
                try {
                    const data = await getCustomUserPin(pinId);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            providesTags: (_, __, pinId) => [{ type: 'CustomUserPin', id: pinId }],
        }),

        // --- Creator Tool Endpoints ---

        // Create map location feature (map pin)
        createMapLocationFeature: builder.mutation<
            MapDataLocationFeature,
            { gameMapExternalId: string; location: NewMapDataLocationFeature }
        >({
            queryFn: async ({ gameMapExternalId, location }) => {
                try {
                    const data = await createGameMapLocationFeature(gameMapExternalId, location);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { gameMapExternalId }) => [{ type: 'MapData', id: gameMapExternalId }],
        }),

        // Update map location feature (map pin)
        updateMapLocationFeature: builder.mutation<
            MapDataLocationFeature,
            { location: MapDataLocationFeature; gameMapExternalId: string }
        >({
            queryFn: async ({ location }) => {
                try {
                    const data = await updateGameMapLocationFeature(location);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { gameMapExternalId }) => [
                { type: 'MapData', id: gameMapExternalId },
                { type: 'MapConfig', id: gameMapExternalId },
            ],
        }),

        // Delete map location feature (map pin)
        deleteMapLocationFeature: builder.mutation<
            null,
            { location: MapDataLocationFeature; gameMapExternalId: string }
        >({
            queryFn: async ({ location }) => {
                try {
                    await deleteGameMapLocationFeature(location);
                    return { data: null };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { gameMapExternalId }) => [
                { type: 'MapData', id: gameMapExternalId },
                { type: 'MapConfig', id: gameMapExternalId },
            ],
        }),

        bulkMoveMapPins: builder.mutation<
            MapData['geojson'],
            {
                gameMapExternalId: string;
                anchorPinExternalId: string;
                geometry: { type: 'Point'; coordinates: [number, number] };
            }
        >({
            queryFn: async ({ gameMapExternalId, anchorPinExternalId, geometry }) => {
                try {
                    const data = await bulkMoveGameMapPins(gameMapExternalId, {
                        anchorPinExternalId,
                        geometry,
                    });
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { gameMapExternalId }) => [
                { type: 'MapData', id: gameMapExternalId },
                { type: 'MapConfig', id: gameMapExternalId },
            ],
        }),

        bulkUpdateMapPins: builder.mutation<
            MapData['geojson'],
            {
                gameMapExternalId: string;
                pins: readonly BulkUpdateGameMapPin[];
            }
        >({
            queryFn: async ({ gameMapExternalId, pins }) => {
                try {
                    const data = await bulkUpdateGameMapPins(gameMapExternalId, pins);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { gameMapExternalId }) => [
                { type: 'MapData', id: gameMapExternalId },
                { type: 'MapConfig', id: gameMapExternalId },
            ],
        }),

        // Create map category
        createMapCategory: builder.mutation<
            MapDataCategory,
            { mapExternalId: string; category: Omit<MapDataCategory, 'externalId'> }
        >({
            queryFn: async ({ mapExternalId, category }) => {
                try {
                    const data = await createGameMapCategory(mapExternalId, category);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { mapExternalId }) => [
                { type: 'MapData', id: mapExternalId },
                { type: 'MapConfig', id: mapExternalId },
            ],
        }),

        // Update map category
        updateMapCategory: builder.mutation<MapDataCategory, { category: MapDataCategory; mapExternalId: string }>({
            queryFn: async ({ category }) => {
                try {
                    const data = await updateGameMapCategory(category);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { mapExternalId }) => [
                { type: 'MapData', id: mapExternalId },
                { type: 'MapConfig', id: mapExternalId },
            ],
        }),

        // Delete map category
        deleteMapCategory: builder.mutation<null, { externalCategoryId: string; mapExternalId: string }>({
            queryFn: async ({ externalCategoryId }) => {
                try {
                    await deleteGameMapCategory(externalCategoryId);
                    return { data: null };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { mapExternalId }) => [
                { type: 'MapData', id: mapExternalId },
                { type: 'MapConfig', id: mapExternalId },
            ],
        }),

        // Create map subcategory
        createMapSubcategory: builder.mutation<
            MapDataSubcategory,
            { subcategory: Omit<MapDataSubcategory, 'externalId'>; mapExternalId: string }
        >({
            queryFn: async ({ subcategory }) => {
                try {
                    const data = await createGameMapSubcategory(subcategory);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { mapExternalId }) => [
                { type: 'MapData', id: mapExternalId },
                { type: 'MapConfig', id: mapExternalId },
            ],
        }),

        // Update map subcategory
        updateMapSubcategory: builder.mutation<
            MapDataSubcategory,
            { subcategory: MapDataSubcategory; mapExternalId: string }
        >({
            queryFn: async ({ subcategory }) => {
                try {
                    const data = await updateGameMapSubcategory(subcategory);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { mapExternalId }) => [
                { type: 'MapData', id: mapExternalId },
                { type: 'MapConfig', id: mapExternalId },
            ],
        }),

        // Delete map subcategory
        deleteMapSubcategory: builder.mutation<null, { subcategory: MapDataSubcategory; mapExternalId: string }>({
            queryFn: async ({ subcategory }) => {
                try {
                    await deleteGameMapSubcategory(subcategory);
                    return { data: null };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { mapExternalId }) => [
                { type: 'MapData', id: mapExternalId },
                { type: 'MapConfig', id: mapExternalId },
            ],
        }),

        // --- Region Endpoints ---

        // Create map region
        createMapRegion: builder.mutation<MapDataRegion, CreateMapRegionRequest>({
            queryFn: async request => {
                try {
                    const data = await createGameMapRegion(request);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, request) => [
                { type: 'MapData', id: request.gameMapExternalId },
                { type: 'MapRegions', id: request.gameMapExternalId },
            ],
        }),

        // Update map region
        updateMapRegion: builder.mutation<
            MapDataRegion,
            { externalId: string; request: UpdateMapRegionRequest; gameMapExternalId: string }
        >({
            queryFn: async ({ externalId, request }) => {
                try {
                    const data = await updateGameMapRegion(externalId, request);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { gameMapExternalId }) => [
                { type: 'MapData', id: gameMapExternalId },
                { type: 'MapRegions', id: gameMapExternalId },
            ],
        }),

        // Delete map region
        deleteMapRegion: builder.mutation<null, { externalId: string; gameMapExternalId: string }>({
            queryFn: async ({ externalId }) => {
                try {
                    await deleteGameMapRegion(externalId);
                    return { data: null };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { gameMapExternalId }) => [
                { type: 'MapData', id: gameMapExternalId },
                { type: 'MapRegions', id: gameMapExternalId },
            ],
        }),

        // --- Label Endpoints ---

        // Create map label
        createMapLabel: builder.mutation<MapDataLabel, CreateMapLabelRequest>({
            queryFn: async request => {
                try {
                    const data = await createGameMapLabel(request);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, request) => [
                { type: 'MapData', id: request.gameMapExternalId },
                { type: 'MapLabels', id: request.gameMapExternalId },
            ],
        }),

        // Update map label
        updateMapLabel: builder.mutation<
            MapDataLabel,
            { externalId: string; request: UpdateMapLabelRequest; gameMapExternalId: string }
        >({
            queryFn: async ({ externalId, request }) => {
                try {
                    const data = await updateGameMapLabel(externalId, request);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { gameMapExternalId }) => [
                { type: 'MapData', id: gameMapExternalId },
                { type: 'MapLabels', id: gameMapExternalId },
            ],
        }),

        // Delete map label
        deleteMapLabel: builder.mutation<null, { externalId: string; gameMapExternalId: string }>({
            queryFn: async ({ externalId }) => {
                try {
                    await deleteGameMapLabel(externalId);
                    return { data: null };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { gameMapExternalId }) => [
                { type: 'MapData', id: gameMapExternalId },
                { type: 'MapLabels', id: gameMapExternalId },
            ],
        }),

        // Upload icon for label
        uploadLabelIcon: builder.mutation<
            UploadLabelIconResponse,
            { externalId: string; icon: File; gameMapExternalId: string }
        >({
            queryFn: async ({ externalId, icon }) => {
                try {
                    const data = await uploadLabelIcon(externalId, icon);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { gameMapExternalId }) => [
                { type: 'MapData', id: gameMapExternalId },
                { type: 'MapLabels', id: gameMapExternalId },
            ],
        }),

        createMap3dObject: builder.mutation<MapData3dObject, CreateMap3dObjectRequest>({
            queryFn: async request => {
                try {
                    const data = await createMap3dObject(request);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { gameMapExternalId }) => [
                { type: 'MapData', id: gameMapExternalId },
                { type: 'Map3dObjects', id: gameMapExternalId },
            ],
        }),

        updateMap3dObject: builder.mutation<
            MapData3dObject,
            { externalId: string; request: UpdateMap3dObjectRequest; gameMapExternalId: string }
        >({
            queryFn: async ({ externalId, request }) => {
                try {
                    const data = await updateMap3dObject(externalId, request);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { gameMapExternalId }) => [
                { type: 'MapData', id: gameMapExternalId },
                { type: 'Map3dObjects', id: gameMapExternalId },
            ],
        }),

        deleteMap3dObject: builder.mutation<null, { externalId: string; gameMapExternalId: string }>({
            queryFn: async ({ externalId }) => {
                try {
                    await deleteMap3dObject(externalId);
                    return { data: null };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { gameMapExternalId }) => [
                { type: 'MapData', id: gameMapExternalId },
                { type: 'Map3dObjects', id: gameMapExternalId },
            ],
        }),

        upload3dObject: builder.mutation<
            Upload3dObjectResponse,
            { externalId: string; model: File; gameMapExternalId: string }
        >({
            queryFn: async ({ externalId, model }) => {
                try {
                    const data = await upload3dObject(externalId, model);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { gameMapExternalId }) => [
                { type: 'MapData', id: gameMapExternalId },
                { type: 'Map3dObjects', id: gameMapExternalId },
            ],
        }),

        // --- Saved checklist endpoints ---

        listSavedChecklists: builder.query<SavedChecklist[], { mapExternalId: string; token: string }>({
            queryFn: async ({ mapExternalId, token }) => {
                try {
                    const data = await listSavedChecklists(mapExternalId, token);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            providesTags: (_, __, { mapExternalId }) => [{ type: 'SavedChecklists', id: `LIST:${mapExternalId}` }],
        }),

        createSavedChecklist: builder.mutation<
            SavedChecklist,
            { mapExternalId: string; input: CreateSavedChecklistInput; token: string }
        >({
            queryFn: async ({ mapExternalId, input, token }) => {
                try {
                    const data = await createSavedChecklist(mapExternalId, input, token);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { mapExternalId }) => [{ type: 'SavedChecklists', id: `LIST:${mapExternalId}` }],
        }),

        updateSavedChecklist: builder.mutation<
            SavedChecklist,
            { mapExternalId: string; id: string; input: UpdateSavedChecklistInput; token: string }
        >({
            queryFn: async ({ mapExternalId, id, input, token }) => {
                try {
                    const data = await updateSavedChecklist(mapExternalId, id, input, token);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { mapExternalId, id }) => [
                { type: 'SavedChecklists', id: `LIST:${mapExternalId}` },
                { type: 'SavedChecklists', id },
            ],
            // If the token refreshed between the list fetch and this mutation, the cache entry
            // under the old token is gone; patch is a no-op and invalidation tags refetch instead.
            onQueryStarted: async ({ mapExternalId, id, input, token }, { dispatch, queryFulfilled }) => {
                const patch = dispatch(
                    mapsApi.util.updateQueryData('listSavedChecklists', { mapExternalId, token }, draft => {
                        const existing = draft.find(c => c.id === id);
                        if (!existing) return;
                        if (input.title !== undefined) existing.title = input.title;
                        if (input.description !== undefined) existing.description = input.description;
                        if (input.pinIds !== undefined) existing.pinIds = input.pinIds;
                        existing.updatedAt = Date.now();
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patch.undo();
                }
            },
        }),

        deleteSavedChecklist: builder.mutation<null, { mapExternalId: string; id: string; token: string }>({
            queryFn: async ({ mapExternalId, id, token }) => {
                try {
                    await deleteSavedChecklist(mapExternalId, id, token);
                    return { data: null };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: (_, __, { mapExternalId, id }) => [
                { type: 'SavedChecklists', id: `LIST:${mapExternalId}` },
                { type: 'SavedChecklists', id },
            ],
            onQueryStarted: async ({ mapExternalId, id, token }, { dispatch, queryFulfilled }) => {
                const patch = dispatch(
                    mapsApi.util.updateQueryData('listSavedChecklists', { mapExternalId, token }, draft => {
                        const idx = draft.findIndex(c => c.id === id);
                        if (idx >= 0) draft.splice(idx, 1);
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patch.undo();
                }
            },
        }),

        getAIPromptConfig: builder.query<AIPromptConfig, { key: string; titleId?: string }>({
            queryFn: async ({ key, titleId }) => {
                try {
                    const data = await getAIPromptConfig(key, titleId);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            providesTags: (_, __, { key }) => [{ type: 'AIPromptConfig', id: key }],
        }),

        getAuthJwt: builder.query<string, string>({
            queryFn: async service => {
                try {
                    const data = await getAuthJwt(service);
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            providesTags: (_, __, service) => [{ type: 'AuthJwt', id: service }],
        }),

        getMapHeatmap: builder.query<MapHeatmapResponse, { titleId: number | string } & MapHeatmapQuery>({
            queryFn: async ({ titleId, eventTypes, days }) => {
                try {
                    const data = await getMapHeatmap(titleId, { eventTypes, days });
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            providesTags: (_, __, { titleId }) => [{ type: 'MapHeatmap', id: String(titleId) }],
        }),
    }),
});

export const {
    useGetGameMapQuery,
    useGetGameMapMetadataQuery,
    useGetGameMapsQuery,
    useGetFoundPinsQuery,
    useGetCustomMarkersQuery,
    useGetCustomUserPinQuery,
    useTogglePinFoundMutation,
    useResetAllFoundPinsMutation,
    useBulkUpdatePinStatusesMutation,
    useCreateCustomMarkerMutation,
    useUpdateCustomMarkerMutation,
    useDeleteCustomMarkerMutation,
    useConvertCustomMarkerCoordinatesMutation,
    // Creator tool hooks
    useCreateMapLocationFeatureMutation,
    useUpdateMapLocationFeatureMutation,
    useDeleteMapLocationFeatureMutation,
    useBulkMoveMapPinsMutation,
    useBulkUpdateMapPinsMutation,
    useCreateMapCategoryMutation,
    useUpdateMapCategoryMutation,
    useDeleteMapCategoryMutation,
    useCreateMapSubcategoryMutation,
    useUpdateMapSubcategoryMutation,
    useDeleteMapSubcategoryMutation,
    // Region hooks
    useCreateMapRegionMutation,
    useUpdateMapRegionMutation,
    useDeleteMapRegionMutation,
    // Label hooks
    useCreateMapLabelMutation,
    useUpdateMapLabelMutation,
    useDeleteMapLabelMutation,
    useUploadLabelIconMutation,
    // 3D Object hooks
    useCreateMap3dObjectMutation,
    useUpdateMap3dObjectMutation,
    useDeleteMap3dObjectMutation,
    useUpload3dObjectMutation,
    // Saved checklist hooks
    useListSavedChecklistsQuery,
    useCreateSavedChecklistMutation,
    useUpdateSavedChecklistMutation,
    useDeleteSavedChecklistMutation,
    // AI Prompt hooks
    useGetAIPromptConfigQuery,
    useLazyGetAuthJwtQuery,
    useGetMapHeatmapQuery,
} = mapsApi;
